/**
 * ScenarioToggle — compact Original ↔ Scenario switch for the navbar.
 *
 * Only ever renders TWO chips regardless of how many scenarios exist, so long
 * scenario names can't overflow the navbar. The right-hand chip shows the active
 * scenario (truncated) and opens a picker modal to choose/manage scenarios.
 *
 * When a scenario is active this is amber — a user must never mistake scenario
 * numbers for the real deal (the Frame renders a matching footer banner).
 */
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { activateScenario } from "Redux/data/actions";
import ScenarioPickerModal, { statusColor } from "Components/ScenarioPickerModal";

const BASE = "base";

const Dot = ({ status }) => (
  <span
    title={status}
    style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: statusColor(status), display: "inline-block", flexShrink: 0 }}
  />
);

const ScenarioToggle = () => {
  const dispatch = useDispatch();
  const scenarios = useSelector((s) => s.project.scenarios) || [];
  const active = useSelector((s) => s.data.activeScenario) || BASE;
  const running = useSelector(
    (s) => s.data.runStatus && !s.data.runStatus.done && !s.data.runStatus.error
  );
  const [showPicker, setShowPicker] = useState(false);

  if (scenarios.length === 0) return null;

  const isScenario = active !== BASE;
  const activeMeta = scenarios.find((s) => s.id === active);

  const chip = (isActive, accent) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "0.35rem",
    padding: "0.25rem 0.6rem",
    border: "none",
    cursor: "pointer",
    fontSize: "0.72rem",
    fontWeight: isActive ? 700 : 500,
    backgroundColor: isActive ? (accent ? "#E8A33D" : "#79589F") : "transparent",
    color: isActive ? "#fff" : "var(--ev-text)",
    whiteSpace: "nowrap",
    maxWidth: "260px",
  });

  return (
    <>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          border: `1px solid ${isScenario ? "#E8A33D" : "var(--ev-border)"}`,
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: "var(--ev-white)",
          maxWidth: "320px",
        }}
      >
        <button
          onClick={() => !running && dispatch(activateScenario(BASE))}
          style={chip(!isScenario, false)}
          title="Show the original deal"
        >
          Original
        </button>
        <button
          onClick={() => setShowPicker(true)}
          style={{ ...chip(isScenario, true), borderLeft: "1px solid var(--ev-border)", minWidth: 0 }}
          title={isScenario ? activeMeta?.name || active : "Choose a scenario"}
        >
          {isScenario && <Dot status={activeMeta?.status} />}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
            {isScenario ? activeMeta?.name || active : `Scenarios (${scenarios.length})`}
          </span>
          <i className="fa-solid fa-chevron-down" style={{ fontSize: "0.6rem", opacity: 0.8, flexShrink: 0 }} />
        </button>
      </div>

      {showPicker && <ScenarioPickerModal onClose={() => setShowPicker(false)} />}
    </>
  );
};

export default ScenarioToggle;
